import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  FormControl,
  FormLabel,
  SimpleGrid,
  Stack,
  Switch,
} from '@chakra-ui/react';
import { useFetchAfesPageQuery, useFetchVendorsPageQuery } from '../../api';
import { useAuth } from '../../core';
import {
  AfeAutoComplete,
  AppInput,
  FieldError,
  FormButtonGroup,
  VendorAutoComplete,
} from '../components';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { PostInvoiceRequest } from '../../api/bindings/PostInvoiceRequest';
import { DatePicker } from 'features';

export interface InvoiceCreateFormDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit'> {
  onSubmit: (form: PostInvoiceRequest & { includeRevision: boolean }) => void;
  onCancel: () => void;
}

interface Form extends PostInvoiceRequest {
  includeRevision: boolean;
}

export const InvoiceCreateFormDrawerContent = (
  props: InvoiceCreateFormDrawerContentProps
) => {
  const { onSubmit, onCancel, ...rest } = props;

  // Fetches
  const auth = useAuth();
  const { data: afes } = useFetchAfesPageQuery(
    { all: true, clientId: auth?.user.clientId ?? '0' },
    { skip: !auth?.user.clientId }
  );
  const { data: vendors } = useFetchVendorsPageQuery(
    {
      clientId: auth?.user.clientId || '0',
      all: true,
    },
    { skip: !auth?.user.clientId }
  );

  // State
  const now = Date();
  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      afeId: '0',
      name: '',
      description: null,
      vendorId: '0',
      reference: null,
      invoiceDate: undefined,
      dueDate: undefined,
      receiveDate: undefined,
      serviceThroughDate: undefined,
      paidDate: undefined,
      includeRevision: true,
    },
  });
  const { control, register, handleSubmit, reset, watch } = form;

  const invoiceName = watch('name');

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerContent {...rest}>
          <DrawerCloseButton />
          <DrawerHeader>
            Create New Invoice: {invoiceName || 'Untitled'}
          </DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel htmlFor="afe">AFE:</FormLabel>
                <Controller
                  name="afeId"
                  control={control}
                  rules={{
                    required: 'AFE is required',
                    validate: (value) =>
                      BigInt(value) > 0 || 'Please select an AFE',
                  }}
                  render={(p) => (
                    <AfeAutoComplete
                      id="afe"
                      items={afes?.items}
                      {...p.field}
                    />
                  )}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel htmlFor="vendor">Vendor:</FormLabel>
                <Controller
                  name="vendorId"
                  control={control}
                  rules={{
                    required: 'Vendor is required',
                    validate: (value) =>
                      BigInt(value) > 0 || 'Please select a vendor',
                  }}
                  render={(f) => (
                    <VendorAutoComplete items={vendors?.items} {...f.field} />
                  )}
                />
                <FieldError name="vendorId" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel htmlFor="name">Name:</FormLabel>
                <AppInput
                  id="name"
                  {...register('name', {
                    required: 'Name is required',
                    minLength: {
                      value: 3,
                      message: 'Name must be between 3 and 256 characters.',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Name must be between 3 and 256 characters.',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="description">Description:</FormLabel>
                <AppInput
                  id="description"
                  {...register('description', {
                    minLength: {
                      value: 3,
                      message:
                        'Description must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message:
                        'Description must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="reference">Reference:</FormLabel>
                <AppInput
                  id="reference"
                  {...register('reference', {
                    minLength: {
                      value: 3,
                      message:
                        'Reference must be between 3 and 256 characters in length',
                    },
                    maxLength: {
                      value: 256,
                      message:
                        'Reference must be between 3 and 256 characters in length',
                    },
                  })}
                />
              </FormControl>

              <SimpleGrid columns={[1, 3]} gap={[1, 4]}>
                <FormControl>
                  <FormLabel htmlFor="invoiceDate">Invoice Date:</FormLabel>
                  <DatePicker name="invoiceDate" control={control} />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="receiveDate">Receive Date:</FormLabel>
                  <DatePicker name="receiveDate" control={control} />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="dueDate">Due Date:</FormLabel>
                  <DatePicker name="dueDate" control={control} />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="serviceThroughDate">
                    Service Through Date:
                  </FormLabel>
                  <DatePicker name="serviceThroughDate" control={control} />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="paidDate">Paid:</FormLabel>
                  <DatePicker name="paidDate" control={control} />
                </FormControl>
              </SimpleGrid>
              <FormControl>
                <FormLabel>Include a Revision?</FormLabel>
                <Controller
                  control={control}
                  name="includeRevision"
                  render={(p) => (
                    <Switch
                      size="lg"
                      isChecked={p.field.value}
                      onChange={() => p.field.onChange(!p.field.value)}
                    />
                  )}
                />
              </FormControl>
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              onConfirm={handleSubmit(onSubmit)}
              onCancel={() => {
                reset();
                onCancel();
              }}
            />
          </DrawerFooter>
        </DrawerContent>
      </form>
    </FormProvider>
  );
};
